import { yupResolver } from '@hookform/resolvers/yup'
import { LineLocationPin } from '@qr-front/icons/line-location-pin'
import { GENERATOR_FORM_ANIMATION_DURATION } from 'features/generator/components/GeneratorFormSection'
import { useTranslations } from 'next-intl'
import { forwardRef, useImperativeHandle } from 'react'
import { FormProvider, Resolver, useForm } from 'react-hook-form'
import { validateContentForm } from 'utils/formUtils'

import { transformBackendResponseKey } from '@/config'
import { getStore } from '@/store'
import { TTranslationFunction } from '@/types'

import { AddressSection } from '../CommonSections/AddressSection'
import {
	TContentFormHandle,
	TQrContentFormSection,
} from '../qrContentFormTypes'
import { useSyncFormSectionValidity } from '../useSyncFormSectionValidity'
import {
	addressCityAtom,
	addressCountryAtom,
	addressPostalCodeAtom,
	addressStateAtom,
	addressStreetAtom,
	addressUrlAtom,
} from './addressAtoms'
import { getAddressSchema, TAddressFormFields } from './addressSchema'

export const LOCATION_FORM_ID = 'location'

export const LocationForm = forwardRef<TContentFormHandle>((_, ref) => {
	const t = useTranslations()

	const formDefaultValues: TAddressFormFields = {
		address: {
			street: getStore().get(addressStreetAtom),
			city: getStore().get(addressCityAtom),
			state: getStore().get(addressStateAtom),
			postalCode: getStore().get(addressPostalCodeAtom),
			country: getStore().get(addressCountryAtom),
			url: getStore().get(addressUrlAtom),
		},
	}

	const form = useForm<TAddressFormFields>({
		defaultValues: formDefaultValues,
		mode: 'onTouched',
		resolver: yupResolver(
			getAddressSchema(t),
		) as unknown as Resolver<TAddressFormFields>,
		reValidateMode: 'onChange',
		shouldUnregister: false,
	})

	const {
		formState: { isValid },
		handleSubmit,
	} = form

	useSyncFormSectionValidity({ formId: LOCATION_FORM_ID, isValid })

	useImperativeHandle(
		ref,
		() => ({
			trigger: (property: string, message: string) => {
				const translatedMessage = t(transformBackendResponseKey(message))
				form.setError(property as Parameters<typeof form.setError>[0], {
					message: translatedMessage,
				})
			},
			validate: () =>
				validateContentForm(
					(onValid, onInvalid) => handleSubmit(onValid, onInvalid),
					GENERATOR_FORM_ANIMATION_DURATION * 1.25,
				),
		}),
		[handleSubmit],
	)

	return (
		<form className="flex flex-col gap-2">
			<FormProvider {...form}>
				<AddressSection form={form} />
			</FormProvider>
		</form>
	)
})

LocationForm.displayName = 'LocationForm'

export const locationFormConfig = (
	t: TTranslationFunction,
): TQrContentFormSection => ({
	id: LOCATION_FORM_ID,
	title: t('generator__content_form_section__location__title'),
	description: t('generator__content_form_section__location__description'),
	icon: <LineLocationPin />,
	Content: LocationForm,
})
